import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Phone,
  Mail,
  LogOut,
  UserPlus,
  CheckCircle2,
  CircleDashed,
  Lock,
  RefreshCw,
  Calendar,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      onLogin(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full border border-white/15 bg-[#0A0C10] px-4 py-3.5 text-base text-white placeholder:text-slate-600 outline-none transition-all duration-300 focus:border-[#0052FF] focus:shadow-[0_0_0_3px_rgba(0,82,255,0.25)]";

  return (
    <div className="flex min-h-[80vh] items-center justify-center px-5 pt-16">
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        data-testid="admin-login-form"
        className="w-full max-w-sm border border-white/10 bg-[#121721] p-8 sm:p-10"
      >
        <span className="flex h-12 w-12 items-center justify-center bg-[#0052FF]">
          <Lock className="h-6 w-6 text-white" />
        </span>
        <h1 className="mt-6 font-display text-4xl font-extrabold uppercase tracking-tight">
          Admin<span className="text-[#0052FF]">.</span>
        </h1>
        <p className="mt-2 text-sm text-slate-400">
          Sign in to see the quote requests.
        </p>
        <div className="mt-8 space-y-5">
          <div>
            <label
              htmlFor="admin-email"
              className="mb-2 block font-tech text-[11px] uppercase tracking-[0.25em] text-slate-400"
            >
              Email
            </label>
            <input
              id="admin-email"
              data-testid="admin-email-input"
              type="email"
              className={inputCls}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />
          </div>
          <div>
            <label
              htmlFor="admin-password"
              className="mb-2 block font-tech text-[11px] uppercase tracking-[0.25em] text-slate-400"
            >
              Password
            </label>
            <input
              id="admin-password"
              data-testid="admin-password-input"
              type="password"
              className={inputCls}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
            />
          </div>
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={busy}
            className="w-full bg-[#0052FF] px-8 py-4 font-display text-xl font-bold uppercase tracking-wider text-white transition-all duration-300 hover:bg-[#2E74FF] hover:shadow-[0_0_36px_rgba(0,82,255,0.6)] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {busy ? "Signing in..." : "Sign in"}
          </button>
        </div>
      </motion.form>
    </div>
  );
}

function JobPhoto({ path, id }) {
  const [url, setUrl] = useState(null);

  useEffect(() => {
    let objectUrl = null;
    axios
      .get(`${API}/files/${path}`, { withCredentials: true, responseType: "blob" })
      .then((r) => {
        objectUrl = URL.createObjectURL(r.data);
        setUrl(objectUrl);
      })
      .catch(() => {});
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path]);

  if (!url) return null;
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      data-testid={`admin-quote-photo-${id}`}
      className="mt-3 block w-fit"
    >
      <img
        src={url}
        alt="Photo of the job"
        className="h-28 w-full max-w-[220px] border border-white/15 object-cover transition-transform duration-300 hover:scale-[1.03]"
      />
      <span className="mt-1 block font-tech text-[10px] uppercase tracking-[0.2em] text-slate-500">
        Click to enlarge
      </span>
    </a>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [quotes, setQuotes] = useState([]);
  const [loadingQuotes, setLoadingQuotes] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [invitePassword, setInvitePassword] = useState("");
  const [inviting, setInviting] = useState(false);
  const [showInvite, setShowInvite] = useState(false);

  const loadQuotes = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/quotes`, {
        withCredentials: true,
      });
      setQuotes(data);
    } catch (err) {
      if (err.response?.status === 401) {
        setUser(false);
      } else {
        toast.error("Couldn't load the quote requests.");
      }
    } finally {
      setLoadingQuotes(false);
    }
  };

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => setUser(r.data))
      .catch(() => setUser(false))
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (user) loadQuotes();
  }, [user]);

  const markDone = async (q) => {
    const next = q.status === "done" ? "new" : "done";
    try {
      await axios.patch(
        `${API}/admin/quotes/${q.id}`,
        { status: next },
        { withCredentials: true }
      );
      setQuotes((qs) =>
        qs.map((item) => (item.id === q.id ? { ...item, status: next } : item))
      );
    } catch {
      toast.error("Couldn't update that one.");
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    setUser(false);
  };

  const invite = async (e) => {
    e.preventDefault();
    if (inviting) return;
    setInviting(true);
    try {
      await axios.post(
        `${API}/admin/invite`,
        { email: inviteEmail, password: invitePassword },
        { withCredentials: true }
      );
      toast.success(`${inviteEmail} can now sign in as admin.`);
      setInviteEmail("");
      setInvitePassword("");
      setShowInvite(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setInviting(false);
    }
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center pt-16">
        <p className="font-tech text-xs uppercase tracking-[0.3em] text-slate-500">
          Checking...
        </p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="pt-16">
        <LoginForm onLogin={setUser} />
      </div>
    );
  }

  return (
    <div className="min-h-screen px-5 pb-24 pt-24 sm:px-8">
      <div className="mx-auto max-w-5xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-tech text-xs uppercase tracking-[0.3em] text-[#4D8DFF]">
              Admin
            </p>
            <h1 className="mt-2 font-display text-4xl font-extrabold uppercase tracking-tight sm:text-5xl">
              Quote requests
            </h1>
            <p className="mt-2 font-tech text-[11px] uppercase tracking-[0.2em] text-slate-500">
              Signed in — {user.email}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={loadQuotes}
              data-testid="admin-refresh-button"
              className="inline-flex items-center gap-2 border border-white/20 px-4 py-2.5 font-tech text-xs uppercase tracking-widest text-slate-300 transition-colors hover:border-[#0052FF] hover:text-white"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </button>
            <button
              onClick={() => setShowInvite((s) => !s)}
              data-testid="admin-invite-toggle"
              className="inline-flex items-center gap-2 bg-[#0052FF] px-4 py-2.5 font-tech text-xs uppercase tracking-widest text-white transition-all duration-300 hover:bg-[#2E74FF]"
            >
              <UserPlus className="h-4 w-4" />
              Add admin
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 border border-white/20 px-4 py-2.5 font-tech text-xs uppercase tracking-widest text-slate-300 transition-colors hover:border-red-500 hover:text-red-400"
            >
              <LogOut className="h-4 w-4" />
              Log out
            </button>
          </div>
        </div>

        {showInvite && (
          <form
            onSubmit={invite}
            data-testid="admin-invite-form"
            className="mt-6 border border-[#0052FF]/40 bg-[#121721] p-6"
          >
            <p className="font-display text-xl font-bold uppercase tracking-wide">
              Give someone admin access
            </p>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <input
                data-testid="admin-invite-email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="their@email.com"
                required
                className="w-full border border-white/15 bg-[#0A0C10] px-4 py-3 text-base text-white placeholder:text-slate-600 outline-none focus:border-[#0052FF]"
              />
              <input
                data-testid="admin-invite-password"
                type="password"
                value={invitePassword}
                onChange={(e) => setInvitePassword(e.target.value)}
                placeholder="Their password (8+ characters)"
                required
                minLength={8}
                className="w-full border border-white/15 bg-[#0A0C10] px-4 py-3 text-base text-white placeholder:text-slate-600 outline-none focus:border-[#0052FF]"
              />
            </div>
            <button
              type="submit"
              data-testid="admin-invite-submit"
              disabled={inviting}
              className="mt-4 bg-[#0052FF] px-6 py-3 font-display text-lg font-bold uppercase tracking-wider text-white transition-all duration-300 hover:bg-[#2E74FF] disabled:opacity-60"
            >
              {inviting ? "Adding..." : "Add admin"}
            </button>
          </form>
        )}

        <div className="mt-10 space-y-4">
          {loadingQuotes ? (
            <p className="font-tech text-xs uppercase tracking-[0.3em] text-slate-500">
              Loading...
            </p>
          ) : quotes.length === 0 ? (
            <p
              data-testid="admin-quotes-empty"
              className="border border-white/10 bg-[#121721] p-8 text-center text-slate-400"
            >
              No quote requests yet — they'll land here the moment someone
              submits the form.
            </p>
          ) : (
            quotes.map((q) => (
              <div
                key={q.id}
                data-testid={`admin-quote-card-${q.id}`}
                className={`border bg-[#121721] p-6 transition-all duration-300 ${
                  q.status === "done"
                    ? "border-white/10 opacity-60"
                    : "border-[#0052FF]/40"
                }`}
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-display text-2xl font-bold uppercase tracking-wide text-white">
                      {q.name}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm">
                      <a
                        href={`tel:${q.phone.replace(/\s/g, "")}`}
                        data-testid={`admin-quote-call-${q.id}`}
                        className="inline-flex items-center gap-2 font-semibold text-[#7EB0FF] hover:text-white"
                      >
                        <Phone className="h-4 w-4" />
                        {q.phone}
                      </a>
                      {q.preferred_date && (
                        <span
                          data-testid={`admin-quote-date-${q.id}`}
                          className="inline-flex items-center gap-2 font-tech text-xs uppercase tracking-widest text-[#7EB0FF]"
                        >
                          <Calendar className="h-4 w-4" />
                          Needed: {q.preferred_date}
                        </span>
                      )}
                      <span className="inline-flex items-center gap-2 font-tech text-xs uppercase tracking-widest text-slate-500">
                        {new Date(q.created_at).toLocaleString("en-AU", {
                          dateStyle: "medium",
                          timeStyle: "short",
                        })}
                      </span>
                    </div>
                    <p className="mt-3 text-base text-slate-300">{q.job}</p>
                    {q.photo_path && (
                      <JobPhoto path={q.photo_path} id={q.id} />
                    )}
                  </div>
                  <button
                    onClick={() => markDone(q)}
                    data-testid={`admin-quote-status-${q.id}`}
                    className={`inline-flex items-center gap-2 px-4 py-2.5 font-display text-sm font-bold uppercase tracking-wider transition-all duration-300 ${
                      q.status === "done"
                        ? "border border-white/20 text-slate-300 hover:text-white"
                        : "bg-[#0052FF] text-white hover:bg-[#2E74FF]"
                    }`}
                  >
                    {q.status === "done" ? (
                      <CheckCircle2 className="h-4 w-4" />
                    ) : (
                      <CircleDashed className="h-4 w-4" />
                    )}
                    {q.status === "done" ? "Done" : "Mark done"}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}